// config/env.js - ReactJS (web) with correct priority order

// Default configuration
const DEFAULT_CONFIG = {
  PORT: '8280',
  BASE_PATH: '/api/v1',
  TIMEOUT: 30000,
  USE_SSL: false,
};

// Helper to get environment variables in ReactJS
const getEnvVar = (key) => {
  // Try process.env (for Create React App, Vite, etc.)
  if (process.env && process.env[key]) {
    return process.env[key];
  }
  // Try window._env_ for runtime configuration (optional)
  if (typeof window !== 'undefined' && window._env_ && window._env_[key]) {
    return window._env_[key];
  }
  return null;
};

// Parse URL helper
const parseUrl = (urlString) => {
  try {
    // Handle URLs without protocol
    if (!urlString.startsWith('http://') && !urlString.startsWith('https://')) {
      urlString = `https://${urlString}`;
    }
    return new URL(urlString);
  } catch (e) {
    return null;
  }
};

export const API_CONFIG = {
  // SERVER_IP - Priority: REACT_APP_SERVER_URL first, then REACT_APP_RENDER_SERVER
  get SERVER_IP() {
    // Priority 1: Check REACT_APP_SERVER_URL first (for local development)
    const serverUrl = getEnvVar('REACT_APP_SERVER_URL');
    if (serverUrl) {
      const url = parseUrl(serverUrl);
      if (url) {
        return url.hostname;
      }
      // If parsing fails, try to extract IP
      const match = serverUrl.match(/\/\/([\d.]+)/);
      if (match) {
        return match[1];
      }
      return serverUrl.replace(/^https?:\/\//, '').split(':')[0];
    }
    
    // Priority 2: Check REACT_APP_RENDER_SERVER (for production)
    const renderServer = getEnvVar('REACT_APP_RENDER_SERVER');
    if (renderServer) {
      const url = parseUrl(renderServer);
      if (url) {
        return url.hostname;
      }
      // If parsing fails, try to use as is
      return renderServer.replace(/^https?:\/\//, '').split(':')[0];
    }
    
    // Priority 3: Use REACT_APP_SERVER_IP
    return getEnvVar('REACT_APP_SERVER_IP') || 'localhost';
  },
  
  // PORT - Priority: REACT_APP_SERVER_URL first, then REACT_APP_RENDER_SERVER
  get PORT() {
    // Priority 1: Check REACT_APP_SERVER_URL first
    const serverUrl = getEnvVar('REACT_APP_SERVER_URL');
    if (serverUrl) {
      const url = parseUrl(serverUrl);
      if (url) {
        return url.port || (url.protocol === 'https:' ? '443' : '80');
      }
      // Try to extract port from string
      const match = serverUrl.match(/:(\d+)/);
      if (match) {
        return match[1];
      }
    }
    
    // Priority 2: Check REACT_APP_RENDER_SERVER
    const renderServer = getEnvVar('REACT_APP_RENDER_SERVER');
    if (renderServer) {
      const url = parseUrl(renderServer);
      if (url) {
        return url.port || (url.protocol === 'https:' ? '443' : '80');
      }
      // Try to extract port from string
      const match = renderServer.match(/:(\d+)/);
      if (match) {
        return match[1];
      }
    }
    
    // Priority 3: Use REACT_APP_BACKEND_PORT or default
    return getEnvVar('REACT_APP_BACKEND_PORT') || DEFAULT_CONFIG.PORT;
  },
  
  // BASE_PATH - Priority: REACT_APP_SERVER_URL first, then REACT_APP_RENDER_SERVER
  get BASE_PATH() {
    // Priority 1: Check REACT_APP_SERVER_URL first
    const serverUrl = getEnvVar('REACT_APP_SERVER_URL');
    if (serverUrl) {
      const url = parseUrl(serverUrl);
      if (url) {
        const pathname = url.pathname;
        if (pathname && pathname.includes('/api/v1')) {
          return pathname;
        }
        if (pathname && pathname.endsWith('/api/v1')) {
          return pathname;
        }
        return DEFAULT_CONFIG.BASE_PATH;
      }
    }
    
    // Priority 2: Check REACT_APP_RENDER_SERVER
    const renderServer = getEnvVar('REACT_APP_RENDER_SERVER');
    if (renderServer) {
      const url = parseUrl(renderServer);
      if (url) {
        const pathname = url.pathname;
        if (pathname && pathname.includes('/api/v1')) {
          return pathname;
        }
        if (pathname && pathname.endsWith('/api/v1')) {
          return pathname;
        }
        return DEFAULT_CONFIG.BASE_PATH;
      }
    }
    
    // Priority 3: Use REACT_APP_BASE_PATH or default
    return getEnvVar('REACT_APP_BASE_PATH') || DEFAULT_CONFIG.BASE_PATH;
  },
  
  // USE_SSL - Priority: REACT_APP_SERVER_URL first, then REACT_APP_RENDER_SERVER
  get USE_SSL() {
    // Priority 1: Check REACT_APP_SERVER_URL first
    const serverUrl = getEnvVar('REACT_APP_SERVER_URL');
    if (serverUrl) {
      const url = parseUrl(serverUrl);
      if (url) {
        return url.protocol === 'https:';
      }
      // Check if it starts with https
      return serverUrl.toLowerCase().startsWith('https');
    }
    
    // Priority 2: Check REACT_APP_RENDER_SERVER
    const renderServer = getEnvVar('REACT_APP_RENDER_SERVER');
    if (renderServer) {
      const url = parseUrl(renderServer);
      if (url) {
        return url.protocol === 'https:';
      }
      // Check if it starts with https
      return renderServer.toLowerCase().startsWith('https');
    }
    
    // Priority 3: Use REACT_APP_USE_SSL or default
    const useSSL = getEnvVar('REACT_APP_USE_SSL');
    return useSSL === 'true' || useSSL === '1' || DEFAULT_CONFIG.USE_SSL;
  },
  
  // TIMEOUT
  get TIMEOUT() {
    const timeout = getEnvVar('REACT_APP_TIMEOUT');
    return timeout ? parseInt(timeout, 10) : DEFAULT_CONFIG.TIMEOUT;
  },
  
  // FORCE_PRODUCTION - Force production mode
  get FORCE_PRODUCTION() {
    const forceProd = getEnvVar('FORCE_PRODUCTION');
    return forceProd === 'true' || forceProd === '1';
  },
  
  // SERVER_URL - Without /api/v1 (for images, static files)
  get SERVER_URL() {
    // Priority 1: Check REACT_APP_SERVER_URL first
    const serverUrl = getEnvVar('REACT_APP_SERVER_URL');
    if (serverUrl) {
      let baseUrl = serverUrl.replace(/\/$/, '');
      // Remove /api/v1 for image serving
      baseUrl = baseUrl.replace(/\/api\/v1$/, '');
      baseUrl = baseUrl.replace(/\/api\/v1\/$/, '');
      // Ensure protocol is present
      if (!baseUrl.startsWith('http://') && !baseUrl.startsWith('https://')) {
        baseUrl = `http://${baseUrl}`;
      }
      return baseUrl;
    }
    
    // Priority 2: Check REACT_APP_RENDER_SERVER
    const renderServer = getEnvVar('REACT_APP_RENDER_SERVER');
    if (renderServer) {
      let baseUrl = renderServer.replace(/\/$/, '');
      // Remove /api/v1 for image serving
      baseUrl = baseUrl.replace(/\/api\/v1$/, '');
      baseUrl = baseUrl.replace(/\/api\/v1\/$/, '');
      // Ensure protocol is present
      if (!baseUrl.startsWith('http://') && !baseUrl.startsWith('https://')) {
        baseUrl = `https://${baseUrl}`;
      }
      return baseUrl;
    }
    
    // Priority 3: Construct from individual parts
    const protocol = this.USE_SSL ? 'https' : 'http';
    const serverIp = this.SERVER_IP;
    const port = this.PORT && ![80, 443].includes(parseInt(this.PORT)) ? `:${this.PORT}` : '';
    return `${protocol}://${serverIp}${port}`;
  },
  
  // BASE_URL - With /api/v1 (for API calls)
  get BASE_URL() {
    // Priority 1: Check REACT_APP_SERVER_URL first
    const serverUrl = getEnvVar('REACT_APP_SERVER_URL');
    if (serverUrl) {
      let baseUrl = serverUrl.replace(/\/$/, '');
      // Ensure /api/v1 is included
      if (!baseUrl.includes('/api/v1')) {
        baseUrl = `${baseUrl}${DEFAULT_CONFIG.BASE_PATH}`;
      }
      return baseUrl;
    }
    
    // Priority 2: Check REACT_APP_RENDER_SERVER
    const renderServer = getEnvVar('REACT_APP_RENDER_SERVER');
    if (renderServer) {
      let baseUrl = renderServer.replace(/\/$/, '');
      // Ensure /api/v1 is included
      if (!baseUrl.includes('/api/v1')) {
        baseUrl = `${baseUrl}${DEFAULT_CONFIG.BASE_PATH}`;
      }
      return baseUrl;
    }
    
    // Priority 3: Construct from SERVER_URL and BASE_PATH
    return `${this.SERVER_URL}${this.BASE_PATH}`;
  },
  
  // WS_URL - WebSocket URL
  get WS_URL() {
    // Priority 1: Check REACT_APP_SERVER_URL first
    const serverUrl = getEnvVar('REACT_APP_SERVER_URL');
    if (serverUrl) {
      const wsUrl = serverUrl
        .replace(/^http:\/\//, 'ws://')
        .replace(/^https:\/\//, 'wss://')
        .replace(/\/api\/v1$/, '');
      return wsUrl;
    }
    
    // Priority 2: Check REACT_APP_RENDER_SERVER
    const renderServer = getEnvVar('REACT_APP_RENDER_SERVER');
    if (renderServer) {
      const wsUrl = renderServer
        .replace(/^http:\/\//, 'ws://')
        .replace(/^https:\/\//, 'wss://')
        .replace(/\/api\/v1$/, '');
      return wsUrl;
    }
    
    // Priority 3: Construct from individual parts
    const protocol = this.USE_SSL ? 'wss' : 'ws';
    const serverIp = this.SERVER_IP;
    const port = this.PORT && ![80, 443].includes(parseInt(this.PORT)) ? `:${this.PORT}` : '';
    return `${protocol}://${serverIp}${port}`;
  },
  
  // SOCKET_URL - Alias for SERVER_URL
  get SOCKET_URL() {
    return this.SERVER_URL;
  },
  
  // IS_PRODUCTION - Check if running in production mode
  get IS_PRODUCTION() {
    if (this.FORCE_PRODUCTION) {
      return true;
    }
    const nodeEnv = getEnvVar('NODE_ENV');
    return nodeEnv === 'production';
  },
  
  // Get full URL with path
  getUrl: (path) => {
    const base = API_CONFIG.BASE_URL;
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${base}${normalizedPath}`;
  },
  
  // Get image URL
  getImageUrl: (imagePath) => {
    const base = API_CONFIG.SERVER_URL;
    const normalizedPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    return `${base}${normalizedPath}`;
  },
};

// Helper function to get config for debugging
export const getConfigInfo = () => {
  return {
    serverIp: API_CONFIG.SERVER_IP,
    port: API_CONFIG.PORT,
    basePath: API_CONFIG.BASE_PATH,
    baseUrl: API_CONFIG.BASE_URL,
    serverUrl: API_CONFIG.SERVER_URL,
    wsUrl: API_CONFIG.WS_URL,
    timeout: API_CONFIG.TIMEOUT,
    useSSL: API_CONFIG.USE_SSL,
    isProduction: API_CONFIG.IS_PRODUCTION,
    forceProduction: API_CONFIG.FORCE_PRODUCTION,
    platform: 'web',
    envVars: {
      NODE_ENV: getEnvVar('NODE_ENV') || 'Not set',
      FORCE_PRODUCTION: getEnvVar('FORCE_PRODUCTION') || 'Not set',
      REACT_APP_SERVER_URL: getEnvVar('REACT_APP_SERVER_URL') || 'Not set',
      REACT_APP_RENDER_SERVER: getEnvVar('REACT_APP_RENDER_SERVER') || 'Not set',
      REACT_APP_SERVER_IP: getEnvVar('REACT_APP_SERVER_IP') || 'Not set',
      REACT_APP_BACKEND_PORT: getEnvVar('REACT_APP_BACKEND_PORT') || 'Not set',
      REACT_APP_BASE_PATH: getEnvVar('REACT_APP_BASE_PATH') || 'Not set',
      REACT_APP_USE_SSL: getEnvVar('REACT_APP_USE_SSL') || 'Not set',
      REACT_APP_TIMEOUT: getEnvVar('REACT_APP_TIMEOUT') || 'Not set',
    }
  };
};

// Validate configuration
export const validateConfig = () => {
  const errors = [];
  const warnings = [];
  
  const renderServer = getEnvVar('REACT_APP_RENDER_SERVER');
  const serverIp = getEnvVar('REACT_APP_SERVER_IP');
  const serverUrl = getEnvVar('REACT_APP_SERVER_URL');
  
  if (!renderServer && !serverIp && !serverUrl) {
    errors.push('No server URL configured (set REACT_APP_RENDER_SERVER, REACT_APP_SERVER_URL, or REACT_APP_SERVER_IP)');
  }
  
  if (renderServer) {
    const url = parseUrl(renderServer);
    if (!url) {
      errors.push(`Invalid REACT_APP_RENDER_SERVER URL: ${renderServer}`);
    } else if (url.protocol !== 'https:' && url.protocol !== 'http:') {
      errors.push(`Invalid protocol in REACT_APP_RENDER_SERVER: ${url.protocol}`);
    }
  }
  
  if (serverUrl) {
    const url = parseUrl(serverUrl);
    if (!url) {
      errors.push(`Invalid REACT_APP_SERVER_URL: ${serverUrl}`);
    }
  }
  
  if (API_CONFIG.TIMEOUT && isNaN(API_CONFIG.TIMEOUT)) {
    errors.push('TIMEOUT must be a number');
  }
  
  if (API_CONFIG.TIMEOUT && API_CONFIG.TIMEOUT < 1000) {
    warnings.push('TIMEOUT is very low (< 1000ms), consider increasing it');
  }
  
  if (API_CONFIG.IS_PRODUCTION && !API_CONFIG.USE_SSL) {
    warnings.push('Running in production mode without SSL');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

// Log config in development
if (process.env.NODE_ENV === 'development') {
  console.log('[Config] 📋 API Config:', getConfigInfo());
  const validation = validateConfig();
  if (!validation.isValid) {
    console.warn('[Config] ⚠️ Config validation errors:', validation.errors);
  }
  if (validation.warnings.length) {
    console.warn('[Config] ⚠️ Config warnings:', validation.warnings);
  }
}

export default API_CONFIG;